import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { ConfigProvider } from "antd";
import enUS from "antd/lib/locale/en_US";
import { IntlProvider } from "react-intl";
import "moment/locale/zh-tw";
import { mgr, logout, isBrowser } from "@services/auth";
import { Log } from "oidc-client";
import useSessionStorage from "./useSessionStorage";
import Store from "./store";
import { ClientUserDto, ClientUserInternationalizationsClient } from "@api";
import enMessages from "../i18n/en";

const adminPageIntelMsg = {
  ReportGroupReportTemplateStaticTypeLabel: "Type",
  ReportGroupReportTemplateStaticMissingTypeRule: "Please select a type",
};

interface StoreProps {
  children: ReactNode;
}

const StoreProvider: FC<StoreProps> = ({ children }) => {
  // --- 状态管理 ---
  const getInitialLang = () => {
    if (!isBrowser) return "en";
    const stored = sessionStorage.getItem("lang");
    return stored ? JSON.parse(stored) : "en";
  };

  const [lang, setLangState] = useState<string>(getInitialLang);
  const [clientUserInfo, setClientUserInfo] = useState<ClientUserDto | null>(
    null,
  );
  const [intlMsg, setIntlMSGState] = useState<Record<string, any>>({
    ...enMessages,
    ...adminPageIntelMsg,
  });
  const [loadingLang, setLoadingLang] = useState<boolean>(false);

  // ---  SessionStorage  ---
  const setLang = (newLang: string) => {
    setLangState(newLang);
    if (isBrowser) {
      sessionStorage.setItem("lang", JSON.stringify(newLang));
    }
  };

  const setIntlMSG = (newMsg: Record<string, any>) => {
    setIntlMSGState(newMsg);
    if (isBrowser) {
      sessionStorage.setItem("i18NMsg", JSON.stringify(newMsg));
    }
  };

  // ---   ---
  useEffect(() => {
    if (!mgr) return;
    const handleTokenExpired = () => {
      try {
        mgr.signinSilent();
      } catch (err) {
        logout();
      }
    };
    const handleSilentRenewError = () => {
      console.log("silent Renew failed");
      logout();
    };
    mgr.events.addAccessTokenExpired(handleTokenExpired);
    mgr.events.addSilentRenewError(handleSilentRenewError);
    return () => {
      mgr.events.removeAccessTokenExpired(handleTokenExpired);
      mgr.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, []);

  // --- 核心逻辑：语言切换时请求数据库 ---
  useEffect(() => {
    const fetchTranslations = async () => {
      setLoadingLang(true);
      try {
        const intlClient = new ClientUserInternationalizationsClient();
        const newIntlMsg = await intlClient.get(lang).catch((err) => {
          console.warn(`Failed to load translations for ${lang}`, err);
          return {};
        });

        const mergedMsg = {
          ...enMessages,
          ...adminPageIntelMsg,
          ...newIntlMsg,
        };

        setIntlMSG(mergedMsg);
      } finally {
        setLoadingLang(false);
      }
    };

    fetchTranslations();
  }, [lang]);

  // --- Context Value ---
  const contextValue = useMemo(
    () => ({
      clientUserInfo,
      intlMsg,
      lang,
      setClientUserInfo,
      setLang,
      setIntlMSG,
    }),
    [clientUserInfo, intlMsg, lang],
  );

  if (loadingLang && Object.keys(intlMsg).length === 0) {
    return <div>Loading language resources...</div>;
  }
  return (
    <Store.Provider value={contextValue}>
      <IntlProvider
        locale={lang}
        key={lang}
        defaultLocale={"en"}
        messages={intlMsg}
      >
        <ConfigProvider dropdownMatchSelectWidth={false} locale={enUS}>
          {children}
        </ConfigProvider>
      </IntlProvider>
    </Store.Provider>
  );
};

export default StoreProvider;
